import { useMemo } from 'react';

import { RunsTable } from '@/components/RunsTable';
import { useDebouncedFilter } from '@/components/RunsTable/hooks/useDebouncedFilter';
import { useSearchModel } from '@/components/RunsTable/hooks/useSearchModel';
import { useDataGridState } from '@/hooks/useDataGridState';
import { useSessions } from '@/hooks/useSwr';

export const TracesTab = ({ evaluatorId }: { evaluatorId: string }) => {
  const { searchModel, setSearchModel } = useSearchModel();

  const { data, isLoading } = useSessions({
    name: 'evaluators',
    include_stats: false,
  });
  const sessionId = data?.rows?.[0]?.id;

  // Create a filter to get all runs with the evaluator's rule_id in metadata
  const runsFilter = useMemo(() => {
    return {
      session: sessionId ? [sessionId] : undefined,
    };
  }, [sessionId]);

  const {
    sortModel,
    columnVisibilityModel,
    paginationModel,
    filterModel,
    setSortModel,
    setColumnVisibilityModel,
    setPaginationModel,
    resetPaginationModel,
    setFilterModel,
    timeModel,
    setTimeModel,
    initialState,
    onColumnVisibilityResetClick,
  } = useDataGridState({
    defaultTimeModel: { duration: '3d' },
  });

  const defaultFilter = `and(eq(metadata_key, "rule_id"), eq(metadata_value, "${evaluatorId}"))`;

  const debouncedRunsFilter = useDebouncedFilter({
    timeModel,
    runsFilter,
    searchModel,
    hiddenFilter: defaultFilter,
  });

  if (!sessionId && !isLoading) {
    return (
      <div className="p-6">
        No 'evaluators' project found - unable to fetch traces
      </div>
    );
  }

  return (
    <div className="flex-grow py-2">
      <RunsTable
        virtual={true}
        view="all"
        tableDisplay="list"
        withTypeCol={true}
        debouncedRunsFilter={debouncedRunsFilter}
        runsFilter={runsFilter}
        searchModel={searchModel}
        setSearchModel={setSearchModel}
        sortModel={sortModel}
        columnVisibilityModel={columnVisibilityModel}
        paginationModel={paginationModel}
        filterModel={filterModel}
        setSortModel={setSortModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        onColumnVisibilityResetClick={onColumnVisibilityResetClick}
        setPaginationModel={setPaginationModel}
        resetPaginationModel={resetPaginationModel}
        setFilterModel={setFilterModel}
        timeModel={timeModel}
        setTimeModel={setTimeModel}
        initialState={initialState}
        isLoadingExternal={isLoading}
        filterBarVariant="run-details"
        className="bg-primary"
        runTableSessionId={sessionId}
      />
    </div>
  );
};
